<template>
  <div class="chat-viewer__info-panel pt-2 pb-2 mt-2 pl-1 pr-1 mb-2 bg-gray-300 font-weight-bold">
    <div v-if="conversation">
      <div class="d-flex justify-content-between">
        <div>
          <el-tooltip
              v-if="inDoNotContact"
              effect="light"
              content="Do Not Contact"
              placement="top-start"
          >
            <span class="text-danger"><i class="fas fa-user-slash"></i></span>
          </el-tooltip>
            <span class="contact-filemaker-id" v-if="contactFilemakerId">{{ contactFilemakerId }}</span>
            <span class="contact-full-name">{{ contactFullName | uppercase }}</span>
            <copy-to-clipboard :value="contactNameWithFilemakerId"></copy-to-clipboard>
        </div>
        <div>
          <span class="outgoing-number-title">{{ outgoingNumberTitle | uppercase }}</span>
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <div>
          <el-tooltip
            v-if="phoneNumberInDoNotContact && ! inDoNotContact"
            effect="light"
            content="Do Not Contact"
            placement="top-start"
          >
            <span class="text-danger"><i class="fas fa-user-slash"></i></span>
          </el-tooltip>
          <template v-if="hasContactPhoneNumber">({{ contactPhoneNumber }} <copy-to-clipboard :value="contactPhoneNumber"></copy-to-clipboard> )</template>
        </div>
        <div>
          ({{ outgoingNumberPhoneNumber }} <copy-to-clipboard :value="outgoingNumberPhoneNumber"></copy-to-clipboard> )
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <div>
          <el-tooltip
            v-if="emailInDoNotContact && ! inDoNotContact"
            effect="light"
            content="Do not contact"
            placement="top-start"
          >
            <span class="text-danger"><i class="fas fa-user-slash"></i></span>
          </el-tooltip>
          <template v-if="hasContactEmail">({{ contactEmail }} <copy-to-clipboard :value="contactEmail"></copy-to-clipboard> )</template>
        </div>
        <div v-if="hasEmailAddress">
          ({{ emailAddress }} <copy-to-clipboard :value="emailAddress"></copy-to-clipboard> )
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <div class="my-1">
          <el-tooltip
              content="Make an outgoing call"
              effect="light"
              placement="top"
          >
            <outbound-call
                v-if="canMakeCall"
                :conversation="conversation"
                class="btn btn-success btn_call btn_call-sm"
            ></outbound-call>
          </el-tooltip>
          <el-tooltip
              content="Add to a group"
              effect="light"
              placement="top"
          >
            <span class="btn btn-secondary btn_call btn_call-sm" style="padding: 0.25rem 0.45rem;"
                  @click="showSendListDialog">
                  <i class="fas fa-users"></i>
                </span>
          </el-tooltip>
          <conversation-preserve :conversation="conversation"></conversation-preserve>
          <el-tooltip
              content="Edit contact"
              effect="light"
              placement="top"
          >
            <span v-if="canEditContact" @click="onEditContact" class="btn btn-secondary btn_call btn_call-sm"
                  style="padding: 0.25rem 0.45rem;">
              <i class="fas fa-edit"></i>
            </span>
          </el-tooltip>
        </div>
      </div>
      <note-viewer :note="note" :store-note-url="storeNoteUrl" :read-only-notes="readOnlyNotes" :can-set-read-only-note="canSetReadOnly"></note-viewer>
    </div>
  </div>
</template>

<script>
import OutboundCall from '../../components/call/outbound-call';
import NoteViewer from './note-viewer';
import CopyToClipboard from '../../components/copy-to-clipboard';
import eventBus from '../../event-bus';
import ConversationPreserve from './conversation-preserve';
import {isFilled} from "../../helpers";

export default {
  components: {
    OutboundCall,
    NoteViewer,
    CopyToClipboard,
    ConversationPreserve,
  },

  props: {
    conversation: Object,
    canMakeCall: Boolean,
  },

  computed: {
    /**
     * @return {Object}
     */
    note() {
      return _.get(this.conversation, 'note');
    },

    /**
     * @return {Object}
     */
    readOnlyNotes() {
      return _.get(this.conversation, 'read_only_notes');
    },

    /**
     * @return {Boolean}
     */
    canSetReadOnly() {
      return _.get(this.conversation, 'canSetReadOnly');
    },

    /**
     * @return {String}
     */
    storeNoteUrl() {
      return _.get(this.conversation, 'storeNoteUrl');
    },

    /**
     * @return {String}
     */
    sendListIndexUrl() {
      return _.get(this.conversation, 'sendListIndexUrl');
    },

    /**
     * @return {Object}
     */
    contact() {
      return _.get(this.conversation, 'contact', null);
    },

    /**
     * @return {String|null}
     */
    contactFullName() {
      return _.get(this.conversation, 'contact.full_name', null);
    },

    /**
     * @return {String|null}
     */
    contactFilemakerId() {
      return _.get(this.conversation, 'contact.filemaker_id', null);
    },

    /**
     * @return {String|null}
     */
    contactNameWithFilemakerId() {
      return _.trim((this.contactFilemakerId || '') + ' ' +  String(this.contactFullName).toUpperCase());
    },

    /**
     * @return {String|null}
     */
    contactPhoneNumber() {
      return _.get(this.conversation, 'contact.phone_number', null);
    },

    /**
     * @return {String|null}
     */
    contactEmail() {
      return _.get(this.conversation, 'contact.email', null);
    },

    /**
     * @return {String|null}
     */
    outgoingNumberTitle() {
      return _.get(this.conversation, 'outgoing_number.title', null);
    },

    /**
     * @return {String|null}
     */
    outgoingNumberPhoneNumber() {
      return _.get(this.conversation, 'outgoing_number.phone_number', null);
    },

    /**
     * @return {*}
     */
    emailAddress() {
      return _.get(this.conversation, 'email_conversation.email_address.address', null);
    },

    /**
     * @returns {boolean}
     */
    inDoNotContact() {
      return this.phoneNumberInDoNotContact && this.emailInDoNotContact;
    },

    phoneNumberInDoNotContact() {
      return _.get(this.conversation, 'do_not_contact', null) !== null;
    },

    /**
     * @return {boolean}
     */
    emailInDoNotContact() {
      return _.get(this.conversation, 'email_conversation.do_not_contact', null) !== null;
    },

    /**
     * @return {Boolean}
     */
    canEditContact() {
      return Boolean(_.get(this.conversation, 'canEditContact'));
    },

    /**
     * @return {boolean}
     */
    hasContactPhoneNumber() {
      return isFilled(_.get(this.conversation, 'contact.phone_number'));
    },

    /**
     * @return {boolean}
     */
    hasContactEmail() {
      return isFilled(_.get(this.conversation, 'contact.email'));
    },

    /**
     * @return {boolean}
     */
    hasOutgoingEmailAddress() {
      return isFilled(_.get(this.conversation, 'email_conversation.email_address.address'));
    },

    /**
     * @return {String}
     */
    emailContext() {
      return _.get(this.conversation, 'contact.email_context', null);
    },

      /**
       * @return {boolean}
       */
    hasEmailAddress() {
      return isFilled(_.get(this.conversation, 'email_conversation.email_address.address'));
    },
  },

  methods: {
    /**
     * @event show-quick-add-to-send-lists
     */
    showSendListDialog() {
      eventBus.$emit('show-quick-add-to-send-lists', {url: this.sendListIndexUrl});
    },

    /**
     * @event show-dialog-edit-contact
     */
    onEditContact() {
      eventBus.$emit('show-dialog-edit-contact', _.get(this.conversation, 'contactEditUrl'));
    },
  },
}
</script>
